import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import CountUp from "react-countup";
import { 
  Search, 
  MapPin, 
  Home, 
  ChevronDown, 
  ArrowUpRight, 
  ShieldCheck,
  Zap,
  BadgePercent,
  Clock,
  Eye,
  Users,
  Building,
  Building2,
  Landmark,
  Trees,
  Layers
} from "lucide-react";

const PROPERTY_TYPES = [
  { key: "house", label: "บ้านเดี่ยว", Icon: Home },
  { key: "townhouse", label: "ทาวน์เฮ้าส์", Icon: Building2 },
  { key: "condo", label: "คอนโด", Icon: Building },
  { key: "commercial", label: "เชิงพาณิชย์", Icon: Landmark },
  { key: "land", label: "ที่ดิน", Icon: Trees },
  { key: "other", label: "อื่นๆ", Icon: Layers },
];

const PRICE_RANGES = [
  { key: "all", label: "ทุกราคา", min: null, max: null },
  { key: "1M", label: "ไม่เกิน 1 ล้าน", min: 0, max: 1000000 },
  { key: "1MT3", label: "1 – 3 ล้าน", min: 1000001, max: 3000000 },
  { key: "3MT5", label: "3 – 5 ล้าน", min: 3000001, max: 5000000 },
  { key: "5MT10", label: "5 – 10 ล้าน", min: 5000001, max: 10000000 },
  { key: "Morethan10m", label: "มากกว่า 10 ล้าน", min: 10000000, max: null },
];

const CATEGORY_ICONS = {
  house: Home,
  townhouse: Building2,
  condo: Building,
  commercial: Landmark,
  land: Trees,
  other: Layers,
};

export default function LandingPage() {
  const token = localStorage.getItem("token");
  const [amountAssets, setAmountAssets] = useState([]);
  const [activeTab, setActiveTab] = useState("ขาย");
  const [heroSlide, setHeroSlide] = useState(0);
  const HERO_IMAGES = ["/hero1.png"];
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(PRICE_RANGES[0]);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const typeDropdownRef = useRef(null);
  const priceDropdownRef = useRef(null);

  const getAssetCounter = useCallback(() => {
    axios
      .get(`${process.env.REACT_APP_API}/event/assetTypeCounter`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const data = res.data.assetCounts;
        const order = ["house", "townhouse", "condo", "commercial", "land", "other"];
        data.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
        setAmountAssets(data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [token]);

  useEffect(() => {
    getAssetCounter();
  }, [getAssetCounter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) setIsTypeOpen(false);
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(e.target)) setIsPriceOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [HERO_IMAGES.length]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set("gridDisplay", "true");
    if (searchKeyword.trim()) params.set("keyword", searchKeyword.trim());
    if (selectedType) params.set("type", selectedType);
    if (selectedPrice.min !== null) params.set("minPrice", selectedPrice.min);
    if (selectedPrice.max !== null) params.set("maxPrice", selectedPrice.max);
    params.set("tab", activeTab);
    localStorage.setItem("AllowOpenGridLayoutFromAutoFilter_IndexPage", true);
    window.location.replace(`/home?${params.toString()}`);
  };

  const handleAutoFilterRealEstate = (key) => {
    if (key !== "") {
      localStorage.setItem("AllowOpenGridLayoutFromAutoFilter_IndexPage", true);
      window.location.replace(`/home?autoFilter=${key}`);
    }
  };

  const handleLinkToSocialMedia = (link) => {
    window.open(link);
  };

  const handleImageError = (e, fallback = "/bg-login.jpg") => {
    e.target.src = fallback;
    e.target.onerror = null;
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] font-['Prompt'] text-slate-900 selection:bg-[#34bfd9]/30 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative flex flex-col lg:flex-row lg:items-start overflow-hidden bg-slate-900
        min-h-screen lg:min-h-[1200px]">

        {/* Background image */}
        <div className="absolute inset-0 z-0">
          {HERO_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`Hero ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
              style={{ opacity: heroSlide === i ? 0.65 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80 lg:bg-gradient-to-r lg:from-black/90 lg:via-black/40 lg:to-transparent" />
        </div>

        {/* ── MOBILE layout ── */}
        <div className="lg:hidden relative z-10 flex flex-col min-h-screen px-5 pt-24 pb-6">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex-1 flex flex-col justify-center"
          >
            {/* Logo */}
            <div className="flex items-center gap-2.5 mb-6">
              <img
                src="/branding/postperty-logo.png"
                alt="Postperty"
                className="w-10 h-10 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-white font-black text-xl tracking-tighter">POSTPERTY</span>
            </div>
            <h1 className="text-4xl font-black text-white tracking-tighter leading-[1.25] uppercase mb-4">
              ซื้อขาย<br />อสังหาฯ<br /><span className="text-[#34bfd9]">บนแผนที่</span>
            </h1>
            <p className="text-sm font-medium text-white/70 leading-relaxed mb-6">
              ดูบ้านแบบ VR 3D · ช่วยแชร์ <span className="text-[#34bfd9] font-bold">ได้ค่าคอมฯ</span>
            </p>
          </motion.div>

          {/* Mobile Search Card — pinned near bottom */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-5">
              {/* Tabs */}
              <div className="flex gap-1.5 mb-4">
                {['ขาย', 'เช่า', 'เซ้ง'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-2 rounded-xl text-sm font-black transition-all ${activeTab === tab ? 'bg-[#34bfd9] text-white' : 'text-slate-400 bg-slate-50'}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Keyword input */}
              <div className="flex items-center gap-3 bg-slate-50 px-4 rounded-2xl border border-slate-100 h-12 mb-3">
                <MapPin className="w-4 h-4 text-[#34bfd9] flex-shrink-0" />
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="ทำเล จังหวัด หรือ รหัสทรัพย์..."
                  className="bg-transparent border-0 p-0 w-full text-sm font-bold text-slate-700 placeholder:text-slate-400 outline-none"
                />
              </div>

              {/* Filter toggle */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-2xl text-sm font-bold text-slate-500 mb-3"
              >
                <span className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-[#34bfd9]" />
                  {selectedType ? PROPERTY_TYPES.find(t => t.key === selectedType)?.label : "ทุกประเภท"}
                  <span className="text-slate-300">·</span>
                  <BadgePercent className="w-4 h-4 text-[#34bfd9]" />
                  {selectedPrice.label}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Expanded filter panel */}
              {isFilterOpen && (
                <div className="mb-3 space-y-3 bg-slate-50 rounded-2xl p-4">
                  {/* ประเภท */}
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">ประเภท</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        onClick={() => setSelectedType(null)}
                        className={`py-1.5 rounded-xl text-xs font-bold transition-all ${!selectedType ? 'bg-[#34bfd9] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
                      >
                        ทั้งหมด
                      </button>
                      {PROPERTY_TYPES.map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => setSelectedType(key)}
                          className={`py-1.5 rounded-xl text-xs font-bold transition-all ${selectedType === key ? 'bg-[#34bfd9] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* ราคา */}
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">ช่วงราคา</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {PRICE_RANGES.map((range) => (
                        <button
                          key={range.key}
                          onClick={() => setSelectedPrice(range)}
                          className={`py-1.5 rounded-xl text-xs font-bold transition-all ${selectedPrice.key === range.key ? 'bg-[#34bfd9] text-white' : 'bg-white text-slate-500 border border-slate-200'}`}
                        >
                          {range.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Search button */}
              <button
                onClick={handleSearch}
                className="w-full h-14 bg-[#34bfd9] text-white rounded-2xl font-black text-lg flex items-center justify-center gap-2.5 hover:bg-[#2aa7bf] active:scale-95 transition-all shadow-lg shadow-[#34bfd9]/25"
              >
                <Search className="w-6 h-6" /> ค้นหาทรัพย์
              </button>
            </div>
          </motion.div>
        </div>

        {/* ── DESKTOP layout (unchanged) ── */}
        <div className="hidden lg:block relative z-10 w-full px-12 pt-36">
          <div className="max-w-[1400px] mx-auto">
            <div className="max-w-4xl">
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <h1 className="text-8xl lg:text-[110px] font-black text-white mb-8 tracking-tighter leading-[1.15] uppercase">
                  ซื้อขาย อสังหาฯ <br />
                  <span className="text-[#34bfd9]">บนแผนที่</span>
                </h1>
                <h2 className="text-3xl font-medium text-white/80 mb-12 max-w-2xl leading-relaxed">
                  ดูบ้านแบบ VR 3D <br />
                  ช่วยชวน ช่วยชี้ ช่วยแชร์ <span className="text-[#34bfd9] font-bold">ได้ค่าคอมฯ</span>
                </h2>
                <button
                  onClick={() => window.location.replace("/home")}
                  className="group flex items-center gap-4 bg-white text-black px-12 py-6 rounded-2xl font-black text-2xl hover:bg-[#34bfd9] hover:text-white transition-all shadow-2xl active:scale-95 mb-16"
                >
                  เข้าใช้งาน Postperty <ArrowUpRight className="w-8 h-8 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </motion.div>

              {/* Desktop Search Box */}
              <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="w-full max-w-6xl">
                <div className="bg-white p-10 rounded-[3rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] border border-slate-100">
                  <div className="flex gap-2 mb-8">
                    {['ขาย', 'เช่า', 'เซ้ง'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === tab ? 'bg-[#34bfd9] text-white shadow-md shadow-[#34bfd9]/30' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="mb-4">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest block pl-1 mb-2">ทำเลที่ตั้ง / คีย์เวิร์ด</label>
                    <div className="flex items-center gap-4 bg-slate-50 px-5 rounded-2xl border border-slate-100 hover:border-[#34bfd9] hover:shadow-lg hover:shadow-[#34bfd9]/5 transition-all h-14">
                      <MapPin className="w-5 h-5 text-[#34bfd9] flex-shrink-0" />
                      <input
                        type="text"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        placeholder="ค้นหาทำเล จังหวัด หรือ รหัสทรัพย์..."
                        className="bg-transparent border-0 p-0 w-full text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:ring-0 outline-none"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 items-end">
                    <div className="relative" ref={typeDropdownRef}>
                      <label className="text-xs font-black text-slate-400 uppercase tracking-widest block pl-1 mb-2">ประเภท</label>
                      <button
                        onClick={() => { setIsTypeOpen(!isTypeOpen); setIsPriceOpen(false); }}
                        className="w-full flex items-center gap-3 bg-slate-50 px-5 rounded-2xl border border-slate-100 hover:border-[#34bfd9] transition-all h-14 text-left"
                      >
                        <Home className="w-5 h-5 text-[#34bfd9] flex-shrink-0" />
                        <span className={`text-sm font-bold flex-1 ${selectedType ? 'text-slate-800' : 'text-slate-400'}`}>
                          {selectedType ? PROPERTY_TYPES.find(t => t.key === selectedType)?.label : "เลือกประเภททรัพย์"}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${isTypeOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isTypeOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-y-auto max-h-56">
                          <button onClick={() => { setSelectedType(null); setIsTypeOpen(false); }} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-sm font-bold text-slate-400 transition-colors">ทุกประเภท</button>
                          {PROPERTY_TYPES.map(({ key, label, Icon }) => (
                            <button key={key} onClick={() => { setSelectedType(key); setIsTypeOpen(false); }} className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-sm font-bold transition-colors ${selectedType === key ? 'text-[#34bfd9]' : 'text-slate-700'}`}>
                              <Icon className="w-4 h-4 text-[#34bfd9]" />{label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative" ref={priceDropdownRef}>
                      <label className="text-xs font-black text-slate-400 uppercase tracking-widest block pl-1 mb-2">ช่วงราคา</label>
                      <button
                        onClick={() => { setIsPriceOpen(!isPriceOpen); setIsTypeOpen(false); }}
                        className="w-full flex items-center gap-3 bg-slate-50 px-5 rounded-2xl border border-slate-100 hover:border-[#34bfd9] transition-all h-14 text-left"
                      >
                        <BadgePercent className="w-5 h-5 text-[#34bfd9] flex-shrink-0" />
                        <span className="text-sm font-bold text-slate-800 flex-1">{selectedPrice.label}</span>
                        <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${isPriceOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isPriceOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-y-auto max-h-56">
                          {PRICE_RANGES.map((range) => (
                            <button key={range.key} onClick={() => { setSelectedPrice(range); setIsPriceOpen(false); }} className={`w-full flex items-center px-5 py-3 hover:bg-slate-50 text-sm font-bold transition-colors ${selectedPrice.key === range.key ? 'text-[#34bfd9]' : 'text-slate-700'}`}>
                              {range.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={handleSearch} className="h-14 bg-[#34bfd9] text-white rounded-2xl font-black text-base flex items-center justify-center gap-3 hover:bg-[#2aa7bf] hover:scale-[1.02] transition-all active:scale-[0.98] shadow-xl shadow-[#34bfd9]/25">
                      <Search className="w-5 h-5" /> ค้นหาทรัพย์
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Slide dots (desktop only) */}
        <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 gap-3 z-20">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroSlide(i)}
              className={`rounded-full transition-all duration-300 ${heroSlide === i ? 'w-8 h-3 bg-[#34bfd9]' : 'w-3 h-3 bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      </section>

      {/* Postperty Platform Section */}
      <section className="py-16 lg:py-40 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center mb-16 lg:mb-40">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-5xl lg:text-[80px] font-black text-slate-900 leading-[1.25] tracking-tighter mb-10">
                PostPerty.com <br />
                <span className="text-[#34bfd9]">Platform ซื้อขาย</span> <br />
                อสังหาฯ <br />
                <span className="text-[#34bfd9]">บนแผนที่</span>
              </h2>
              <p className="text-slate-500 text-2xl font-medium leading-relaxed mb-12">
                เว็บไซต์ซื้อขายอสังหาฯบนแผนที่ ใช้งานง่ายเหมือนมีแผนที่อสังหาฯอยู่ในมือ เลือกทำเลที่ชอบ ค้นหาราคาที่ใช่ ได้สะดวก พร้อมฟีเจอร์ดูบ้านแบบ VR 360 องศา ช่วยลดการนัดเปิดบ้านจริง ประหยัดเวลา ทั้งเจ้าของบ้าน และคนที่สนใจ
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                  <div className="w-14 h-14 bg-[#34bfd9] text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#34bfd9]/20">
                    <Users className="w-8 h-8" />
                  </div>
                  <h4 className="font-black text-xl mb-4">ระบบ Affiliate</h4>
                  <p className="text-slate-400 font-medium">กระจายรายได้สูงสุด 66% ของค่านายหน้า ให้กับสมาชิก สมัครฟรี! ช่วยแชร์ หรือถ่ายรูปป้ายขายทรัพย์ ส่งขึ้นมาบนเว็บ PostPerty ก็มีโอกาสรับค่านายหน้า</p>
                </div>
                <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                  <div className="w-14 h-14 bg-black text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <Zap className="w-8 h-8" />
                  </div>
                  <h4 className="font-black text-xl mb-4">ลงขายฟรี</h4>
                  <p className="text-slate-400 font-medium">เจ้าของบ้านลงขายเองได้ฟรี! ไม่มีค่าใช้จ่าย เพิ่มโอกาสการเข้าถึงลูกค้าด้วยเทคโนโลยีที่ดีที่สุด</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} className="relative lg:max-w-xl lg:mx-auto mt-4 lg:mt-0">
              <div className="rounded-3xl lg:rounded-[4rem] overflow-hidden shadow-[0_30px_60px_-10px_rgba(0,0,0,0.15)] border-4 lg:border-[12px] border-white bg-slate-100 aspect-[4/3]">
                <img src="/mapbase.png" alt="Platform" className="w-full h-full object-cover" />
              </div>
              <div className="hidden lg:block absolute -bottom-10 -left-10 bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-50">
                <p className="font-black text-4xl leading-none">Map-Based</p>
                <p className="text-slate-400 font-bold tracking-widest uppercase text-xs mt-2">Navigation System</p>
              </div>
              {/* Mobile caption */}
              <div className="lg:hidden mt-3 flex items-center gap-2">
                <span className="font-black text-lg text-slate-900">Map-Based</span>
                <span className="text-slate-400 font-bold text-xs uppercase tracking-widest">Navigation System</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Postperty Agent Section */}
      <section className="py-16 lg:py-40 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
              <span className="text-[#34bfd9] font-black text-xs uppercase tracking-[0.4em] mb-6 block">PostPerty Agent Services</span>
              <h2 className="text-5xl lg:text-[70px] font-black text-slate-900 leading-[1.25] lg:leading-[1.15] tracking-tighter uppercase mb-10">
                มืออาชีพ <span className="text-[#34bfd9]">ดูแลทุกขั้นตอน</span>
              </h2>
              <p className="text-slate-500 text-xl lg:text-2xl font-medium leading-relaxed">
                เราคือบริษัทนายหน้าที่มีพนักงานดูแลลูกค้าเป็นพนักงานของเราเองที่พร้อมดูและให้คำปรึกษาตั้งแต่เริ่มจนปิดการขาย คิดค่าบริการจากผู้ฝากขายแค่ 3% เมื่อขายได้เท่านั้น
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="relative mb-10 lg:mb-32"
          >
            {/* <div className="rounded-3xl lg:rounded-[5rem] overflow-hidden shadow-[0_30px_60px_-10px_rgba(0,0,0,0.15)] border-4 lg:border-[20px] border-slate-50 bg-slate-100">
              <img src="/postperty_agent.jpg" className="w-full aspect-[4/3] md:aspect-video lg:aspect-[21/9] object-cover object-center" alt="PostPerty Agent Team" />
            </div> */}

            {/* Mobile info strip */}
            <div className="lg:hidden mt-4 flex items-center justify-between bg-[#34bfd9] rounded-2xl p-4 text-white">
              <div>
                <p className="font-black text-base leading-tight">ทีมงานคุณภาพ</p>
                <p className="text-white/80 text-xs font-bold uppercase tracking-widest mt-0.5">Professional Team Support</p>
              </div>
              <div className="bg-black/20 rounded-xl px-4 py-2 text-center">
                <p className="font-black text-2xl text-white leading-none">3%</p>
                <p className="text-white/70 text-[10px] font-bold uppercase">Commission</p>
              </div>
            </div>

            {/* Desktop floating cards */}
            {/* <div className="absolute -bottom-10 -right-6 lg:-right-10 bg-[#34bfd9] text-white p-8 lg:p-12 rounded-[3rem] shadow-2xl hidden lg:block max-w-sm border-[12px] border-white">
              <p className="text-2xl lg:text-3xl font-black leading-tight mb-4">ทีมงานคุณภาพ พร้อมดูแลคุณ</p>
              <div className="flex items-center gap-4 text-white/80 font-bold uppercase tracking-widest text-xs">
                <Users className="w-6 h-6 text-white" />
                <span>Professional Team Support</span>
              </div>
            </div> */}
            <div className="absolute -top-10 -left-10 bg-black text-white p-8 rounded-[2.5rem] shadow-2xl hidden lg:block border-[10px] border-white rotate-[-5deg]">
              <p className="font-black text-4xl leading-none mb-1 text-[#34bfd9]">3%</p>
              <p className="font-bold text-xs uppercase tracking-tighter">Fixed Commission</p>
            </div>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100 hover:border-[#34bfd9]/30 transition-all group">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-[#34bfd9] mb-8 shadow-sm group-hover:scale-110 transition-transform">
                <BadgePercent className="w-8 h-8" />
              </div>
              <h4 className="font-black text-2xl text-slate-900 mb-4">Commission 3%</h4>
              <p className="text-slate-500 font-medium leading-relaxed">คิดค่าบริการเมื่อขายทรัพย์ได้เท่านั้น โปร่งใส ชัดเจน ไม่มีแอบแฝง</p>
            </div>
            
            <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100 hover:border-[#34bfd9]/30 transition-all group">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-[#34bfd9] mb-8 shadow-sm group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h4 className="font-black text-2xl text-slate-900 mb-4">No Hidden Fees</h4>
              <p className="text-slate-500 font-medium leading-relaxed">ไม่มีการบวกราคาเพิ่มจากเจ้าของบ้าน มั่นใจในราคาตลาดที่แท้จริง</p>
            </div>

            <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100 hover:border-[#34bfd9]/30 transition-all group">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-[#34bfd9] mb-8 shadow-sm group-hover:scale-110 transition-transform">
                <Clock className="w-8 h-8" />
              </div>
              <h4 className="font-black text-2xl text-slate-900 mb-4">24/7 Service</h4>
              <p className="text-slate-500 font-medium leading-relaxed">คอลเซ็นเตอร์ และ Customer Service พร้อมดูแลคุณตลอด 24 ชม.</p>
            </div>
          </div>

          <div className="mt-20 text-center">
            <button onClick={() => window.location.replace("/contact")} className="bg-[#34bfd9] text-white px-16 py-8 rounded-[2rem] font-black text-2xl hover:bg-[#2aa7bf] transition-all active:scale-95 shadow-2xl shadow-[#34bfd9]/25">
              ติดต่อทีมงานเอเจนท์
            </button>
          </div>
        </div>
      </section>

      {/* Postperty 3D Section */}
      <section className="py-16 lg:py-40 bg-white relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* GIF ซ้าย */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}>
              <div className="relative w-full bg-slate-100 overflow-hidden rounded-3xl shadow-2xl" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src="https://giphy.com/embed/QuUhaaIOWkGgDRcEQf"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  className="giphy-embed"
                  allowFullScreen
                  style={{ position: "absolute", pointerEvents: "none", top: 0, left: 0 }}
                  title="3D Experience"
                ></iframe>
              </div>
            </motion.div>

            {/* Text + cards ขวา */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}>
              <span className="text-[#34bfd9] font-black text-xs uppercase tracking-[0.4em] mb-4 block">PostPerty 3D Experience</span>
              <h2 className="text-4xl lg:text-[64px] font-black text-slate-900 leading-[1.25] tracking-tighter uppercase mb-6">
                สัมผัสบ้าน <br /><span className="text-[#34bfd9]">เสมือนจริง</span>
              </h2>
              <p className="text-slate-500 text-base lg:text-lg font-medium leading-relaxed mb-8">
                PostPerty 3D บริการถ่ายสถานที่ด้วยกล้อง 360 องศา สร้างภาพแบบ 3 มิติ เพิ่มโอกาสปิดยอดขายตั้งแต่ช่องทางออนไลน์ ให้ลูกค้าดูบ้านอย่างละเอียดเหมือนเข้ามาดูบ้านจริงผ่านมือถือ
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Eye, title: "เหมือนได้เข้าไปดูด้วยตัวเอง", desc: "ภาพคมชัดทุกมุมมอง 360 องศา เห็นรายละเอียดครบถ้วน" },
                  { icon: Zap, title: "ฟรี! สื่อรีวิวและกราฟิก", desc: "สำหรับผู้ที่เซ็นสัญญาฝากขายกับ PostPerty พร้อมรีวิว Video มืออาชีพ" },
                  { icon: ShieldCheck, title: "เพิ่มโอกาสปิดการขาย", desc: "ลดการนัดเปิดบ้านจริง ลูกค้าดูบ้านได้ทุกที่ทุกเวลา" },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-[#34bfd9] flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-black text-base text-slate-900 mb-0.5">{title}</h5>
                      <p className="text-slate-400 font-medium text-sm">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a href="https://app.cloudpano.com/tours/P1J97cdxlz" target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 text-slate-900 font-black text-lg group border-b-4 border-slate-900 pb-1 hover:text-[#34bfd9] hover:border-[#34bfd9] transition-all">
                คลิกที่นี่เพื่อดูตัวอย่าง <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="bg-slate-50 py-32 px-6 lg:px-12 relative z-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-center mb-16 gap-6 text-center lg:text-left">
            <div>
              <span className="text-[#34bfd9] font-black text-[10px] uppercase tracking-[0.4em] mb-4 block">Premium Selection</span>
              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none mb-3">Popular Categories</h2>
              <p className="text-slate-400 text-lg font-medium">เลือกหมวดหมู่ที่ใช่ สำหรับบ้านในฝันของคุณ</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {amountAssets.map((asset, index) => {
              const IconComponent = CATEGORY_ICONS[asset.key] || CATEGORY_ICONS.other;
              return (
                <motion.div 
                  key={asset.key} 
                  whileHover={{ y: -8, scale: 1.02 }} 
                  onClick={() => handleAutoFilterRealEstate(asset.key)} 
                  className="group cursor-pointer bg-white p-6 rounded-[2rem] shadow-sm hover:shadow-xl hover:shadow-[#34bfd9]/10 transition-all duration-300 border border-slate-100 flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-[#34bfd9] mb-5 group-hover:bg-[#34bfd9] group-hover:text-white transition-all duration-300 shadow-sm">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <div className="space-y-1 mb-4">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-[#34bfd9] transition-colors">{asset.eng}</p>
                    <h3 className="text-lg font-black text-slate-900 group-hover:text-[#34bfd9] transition-colors leading-tight">{asset.thai}</h3>
                  </div>
                  <div className="mt-auto inline-flex items-center gap-1.5 px-4 py-1.5 bg-slate-50 rounded-full group-hover:bg-[#34bfd9]/10 transition-colors">
                    <span className="text-sm font-black text-[#34bfd9]">
                      <CountUp end={parseInt(asset.amount)} duration={2} />
                    </span>
                    <span className="text-[11px] font-bold text-slate-400 group-hover:text-[#34bfd9]/60 transition-colors">รายการ</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white relative overflow-hidden">
        {/* Top accent */}
        <div className="w-full h-1 bg-gradient-to-r from-[#34bfd9] via-[#34bfd9]/50 to-transparent"></div>

        {/* Big CTA strip */}
        <div className="border-b border-white/5 py-16 lg:py-20 px-6 lg:px-12">
          <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase text-center lg:text-left">
              เริ่มต้น<span className="text-[#34bfd9]">ค้นหาบ้าน</span>วันนี้
            </h2>
            <button
              onClick={() => window.location.replace("/home")}
              className="group flex items-center gap-4 bg-[#34bfd9] text-white px-10 py-5 rounded-2xl font-black text-xl hover:bg-white hover:text-black transition-all shadow-2xl shadow-[#34bfd9]/20 active:scale-95 flex-shrink-0"
            >
              เข้าใช้งาน Postperty <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Main footer grid */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16 mb-16">

            {/* Brand */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <img src="/branding/postperty-logo.png" className="w-12 h-12 object-contain" alt="Postperty" />
                <div>
                  <p className="text-xl font-black tracking-tighter leading-none">POSTPERTY</p>
                </div>
              </div>
              <p className="text-white/70 text-sm font-medium leading-relaxed mb-8 max-w-xs">
                เปลี่ยนวิธีซื้อขายอสังหาฯ ให้ง่ายและสนุกกว่าเดิม ด้วยเทคโนโลยีบนแผนที่และทีมงานมืออาชีพ
              </p>
              <div className="flex gap-3">
                {[
                  { icon: 'https://cdn-icons-png.flaticon.com/512/5968/5968764.png', url: 'https://facebook.com/PostpertyDotcom', label: 'Facebook', invert: false },
                  { icon: 'https://cdn-icons-png.flaticon.com/512/3046/3046120.png', url: 'https://tiktok.com/@postperty', label: 'TikTok', invert: true },
                  { icon: 'https://cdn-icons-png.flaticon.com/512/174/174883.png', url: 'https://youtube.com/@postperty', label: 'YouTube', invert: false },
                ].map((s, i) => (
                  <button key={i} onClick={() => handleLinkToSocialMedia(s.url)} title={s.label}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:scale-110 transition-all">
                    <img src={s.icon} className={`w-5 h-5 ${s.invert ? 'invert' : ''}`} alt={s.label} />
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-2">
              <h4 className="text-[#34bfd9] font-black mb-5 uppercase tracking-[0.3em] text-[10px]">เมนู</h4>
              <ul className="space-y-3">
                {[
                  { label: 'หน้าหลัก', href: '/home' },
                  { label: 'ซื้อทรัพย์', href: '/home?gridDisplay=true&tab=ขาย' },
                  { label: 'เช่าทรัพย์', href: '/home?gridDisplay=true&tab=เช่า' },
                  { label: 'เซ้งทรัพย์', href: '/home?gridDisplay=true&tab=เซ้ง' },
                  { label: 'เกี่ยวกับเรา', href: '/help/postperty' },
                ].map(({ label, href }) => (
                  <li key={label}>
                    <a href={href} className="text-white/70 font-bold text-sm hover:text-white transition-colors">{label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <h4 className="text-[#34bfd9] font-black mb-5 uppercase tracking-[0.3em] text-[10px]">บริการ</h4>
              <ul className="space-y-3">
                {[
                  { label: 'PostPerty Agent', href: '/contact' },
                  { label: 'PostPerty 3D', href: '/contact' },
                  { label: 'ลงประกาศฟรี', href: '/post' },
                  { label: 'ระบบ Affiliate', href: '/help/postperty' },
                ].map(({ label, href }) => (
                  <li key={label}>
                    <a href={href} className="text-white/70 font-bold text-sm hover:text-white transition-colors">{label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-4">
              <h4 className="text-[#34bfd9] font-black mb-5 uppercase tracking-[0.3em] text-[10px]">ติดต่อเรา</h4>
              <p className="text-white/70 text-sm font-medium leading-relaxed mb-6">
                88/91 หมู่9 ต.บางแขม อ.เมืองนครปฐม<br />จังหวัดนครปฐม 73000
              </p>
              <div className="group flex items-center gap-4 bg-white/5 p-5 rounded-2xl border border-white/10 hover:border-[#34bfd9]/50 transition-all cursor-pointer">
                <div className="p-3 bg-[#34bfd9]/10 rounded-xl group-hover:bg-[#34bfd9] transition-colors flex-shrink-0">
                  <img src="https://cdn-icons-png.flaticon.com/512/2111/2111463.png" className="w-6 h-6 invert" alt="Line" />
                </div>
                <div>
                  <p className="text-[#34bfd9] font-black text-[10px] uppercase tracking-widest mb-0.5">Official Line</p>
                  <p className="text-white font-black text-lg">@PostPerty</p>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 font-black text-[10px] uppercase tracking-[0.5em]">
              © 2026 POSTPERTY CO., LTD. ALL RIGHTS RESERVED.
            </p>
            <div className="flex gap-6">
              {['นโยบายความเป็นส่วนตัว', 'ข้อกำหนดการใช้งาน'].map((t) => (
                <a key={t} href="/help/postperty" className="text-white/50 font-bold text-[10px] hover:text-white transition-colors">{t}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
